import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../Common/PageHeader/PageHeader';
import { getScoreboardFull } from '../../services/adminApi';
import { updateDocumentTitle } from '../../utils/documentTitle';
import './AdminHubPage.css';

/**
 * AdminScoreboardPage: /admin/scoreboard (Nitish, Sep 16 2026, V0 of the admin hub).
 *
 * V1 (Nitish's ruling, Oct 19 2026: "one place, just the critical numbers, last 30 days, not maintained in two
 * places"): every panel and tile reads the `v1` block of the hourly scoreboard_full cache
 * (daily_report_service._collect_scoreboard_v1). The server builds ONE per-person table and every number here is
 * a count from it, so a figure shown twice is the same figure. People, not events; real signups and duplicate
 * accounts never blend (duplicate is the grey segment; throwaway accounts are left out). ET days, Mon-Sun ET weeks. Payments are V2.
 *
 * Still no chart library: small SVG, hover (tap on phones) for exact numbers, legends carry window totals, and a
 * Numbers table under each chart. Series colours are the dataviz reference palette (validated light and dark on
 * the --cc-surface panels), scoped as --sb-* in AdminHubPage.css.
 */
const etTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET'; } catch (e) { return ''; }
};
const ageText = (d) => {
  if (!d) return '';
  let secs = null;
  if (d.cache && typeof d.cache.age_seconds === 'number') secs = d.cache.age_seconds;
  else if (d.computed_at) secs = Math.max(0, (Date.now() - new Date(d.computed_at).getTime()) / 1000);
  if (secs === null || Number.isNaN(secs)) return 'fresh';
  const mins = Math.round(secs / 60);
  return mins < 1 ? 'computed just now' : `computed ${mins} min ago (${etTime(d.computed_at || (d.cache && d.cache.computed_at))})`;
};

const S = (n) => `var(--sb-${n})`;
const GREY = 'var(--sb-grey)';
const INK = 'var(--sb-ink)';

const H = 160; const H_SHORT = 110; const PAD = { l: 28, r: 6, t: 8, b: 20 };
const IH = H - PAD.t - PAD.b; const IH_SHORT = H_SHORT - PAD.t - PAD.b;
const DEFAULT_W = 560;

/* Charts draw at their REAL pixel width (Nov 1 2026): the old fixed 560-wide canvas with preserveAspectRatio="none"
   stretched every axis number and date sideways on wide screens. Falls back to 560 where width cannot be measured. */
const useChartWidth = (active) => {
  const ref = useRef(null);
  const [w, setW] = useState(DEFAULT_W);
  useEffect(() => {
    const el = ref.current;
    if (!active || !el) return undefined;
    const read = () => { const cw = Math.round(el.clientWidth); if (cw > 0) setW(cw); };
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [active]);
  return [ref, w];
};

const fmtV = (v, pct) => (typeof v === 'number' ? (pct ? `${Math.round(v)}%` : v.toLocaleString()) : '—');
const sumOf = (r, series) => series.reduce((s, x) => s + (r[x.k] || 0), 0);

/* Series swatch: an SVG rect filled exactly like the chart marks. Take 2 (Maeve's review, Oct 19 2026): the old
   <i style={{ background: var(--sb-n) }}> lost its colour wherever the declaration was dropped (jsdom's CSS parser drops a
   var() inside the `background` shorthand, so the rendered/serialized page fell back to the teal .adm-legend i rule);
   `fill` is the property the bars already render with, so swatch and bar can never disagree. */
const Swatch = ({ color, dim = false, size = 10, label }) => (
  <svg className="adm-sb-sw" width={size} height={size} viewBox="0 0 10 10" aria-hidden="true" style={{ fill: color, opacity: dim ? 0.6 : 1 }}
    data-testid={label ? 'sb-swatch' : undefined} data-label={label}>
    <rect width="10" height="10" rx="2" />
  </svg>
);

/* Hover/tap readout: the exact values for one day or week. left is clamped in CSS px so the tip never leaves the
   chart (a % clamp overflowed a 390px phone by a pixel). */
const Tip = ({ leftPct, title, items, pct, total, foot }) => (
  <div className="adm-tip adm-sb-tip" style={{ left: `clamp(90px, ${leftPct}%, calc(100% - 90px))` }} role="status">
    <div className="adm-tip-h">{title}</div>
    {items.map((it) => (
      <div className="adm-tip-r" key={it.label}>
        <Swatch color={it.color} size={8} />{it.label}<b>{fmtV(it.v, pct)}</b>
      </div>
    ))}
    {total && items.length > 1 ? <div className="adm-tip-r adm-tip-t">Total<b>{items.reduce((s, it) => s + (it.v || 0), 0).toLocaleString()}</b></div> : null}
    {foot ? <div className="adm-tip-r adm-tip-t">{foot}</div> : null}
  </div>
);

/* Legend: one swatch per series, with the window total for count charts. */
const Legend = ({ rows, series, totals = true }) => (
  <div className="adm-legend">
    {series.map((s) => (
      <span key={s.k}>
        <Swatch color={s.color} dim={!!s.dash} label={s.label} />{s.label}
        {totals ? <b className="adm-legend-n">{rows.reduce((t, r) => t + (r[s.k] || 0), 0).toLocaleString()}</b> : null}
      </span>
    ))}
  </div>
);

/* Stacked bars. pct = each column is 100% of its own total (share chart); a column with nothing to share (or
   flagged noData) draws "no data" instead of a guess. */
const Bars = ({ rows, series, labelEvery = 5, pct = false, foot }) => {
  const [hi, setHi] = useState(null);
  const [wrapRef, W] = useChartWidth(rows.length > 0);
  if (!rows.length) return null;
  const IW = W - PAD.l - PAD.r;
  const max = pct ? 100 : Math.max(1, ...rows.map((r) => sumOf(r, series)));
  const bw = IW / rows.length;
  const y = (v) => PAD.t + IH - (v / max) * IH;
  const empty = (r) => r.noData || (pct && sumOf(r, series) === 0);
  const hiRow = hi !== null ? rows[hi] : null;
  return (
    <div className="adm-chart-wrap" ref={wrapRef} onMouseLeave={() => setHi(null)}>
      <svg className="adm-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="bar chart">
        <line className="axis" x1={PAD.l} x2={W - PAD.r} y1={PAD.t + IH} y2={PAD.t + IH} />
        <text x={2} y={PAD.t + 8}>{pct ? '100%' : max}</text>
        {rows.map((r, i) => {
          const x = PAD.l + i * bw + 1; const w = Math.max(1, bw - 2);
          const tot = sumOf(r, series);
          let acc = 0;
          return (
            <g key={r.id || i} className={hi === null ? '' : hi === i ? 'on' : 'dim'}>
              {empty(r) ? <text className="adm-sb-nodata" x={x + w / 2} y={PAD.t + IH - 6} textAnchor="middle">no data</text> : series.map((s) => {
                const raw = r[s.k] || 0;
                if (!raw) return null;
                const v = pct ? (raw / tot) * 100 : raw;
                const y0 = y(acc); acc += v; const y1 = y(acc);
                return <rect key={s.k} className="seg" x={x} width={w} y={y1} height={Math.max(0, y0 - y1)} style={{ fill: s.color }} />;
              })}
              {i % labelEvery === 0 ? <text x={x} y={H - 6}>{r.label}</text> : null}
              <rect className="hit" data-testid="sb-bar-hit" x={PAD.l + i * bw} width={bw} y={PAD.t} height={IH} onMouseEnter={() => setHi(i)} onClick={() => setHi(i)} />
            </g>
          );
        })}
      </svg>
      {hiRow ? (
        <Tip leftPct={((PAD.l + (hi + 0.5) * bw) / W) * 100} title={hiRow.tip || hiRow.label} total={!pct} pct={pct && !empty(hiRow)}
          foot={foot ? foot(hiRow) : null}
          items={empty(hiRow) ? [] : series.map((s) => {
            const tot = sumOf(hiRow, series);
            return { label: s.label, color: s.color, v: pct ? ((hiRow[s.k] || 0) / tot) * 100 : (hiRow[s.k] || 0) };
          })} />
      ) : null}
    </div>
  );
};

const Lines = ({ rows, series }) => {
  const [hi, setHi] = useState(null);
  const [wrapRef, W] = useChartWidth(rows.length > 0);
  if (!rows.length) return null;
  const IW = W - PAD.l - PAD.r;
  const vals = rows.flatMap((r) => series.map((s) => r[s.k])).filter((v) => typeof v === 'number');
  const max = Math.max(1, ...vals);
  const x = (i) => PAD.l + (rows.length === 1 ? IW / 2 : (i / (rows.length - 1)) * IW);
  const y = (v) => PAD.t + IH_SHORT - (v / max) * IH_SHORT;
  const path = (k) => rows.map((r, i) => (typeof r[k] === 'number' ? `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(r[k]).toFixed(1)}` : '')).join(' ');
  const step = rows.length > 1 ? IW / (rows.length - 1) : IW;
  return (
    <div className="adm-chart-wrap" ref={wrapRef} onMouseLeave={() => setHi(null)}>
      <svg className="adm-chart adm-sb-short" viewBox={`0 0 ${W} ${H_SHORT}`} role="img" aria-label="line chart">
        <line className="axis" x1={PAD.l} x2={W - PAD.r} y1={PAD.t + IH_SHORT} y2={PAD.t + IH_SHORT} />
        <text x={2} y={PAD.t + 8}>{max}</text>
        {series.map((s) => (
          <path key={s.k} d={path(s.k)} style={{ fill: 'none', stroke: s.color, strokeWidth: 2, strokeDasharray: s.dash ? '4 4' : 'none' }} vectorEffect="non-scaling-stroke" />
        ))}
        {hi !== null ? <line className="axis" x1={x(hi)} x2={x(hi)} y1={PAD.t} y2={PAD.t + IH_SHORT} /> : null}
        {rows.map((r, i) => <text key={`l${r.id || i}`} x={Math.min(x(i) - 10, W - 34)} y={H_SHORT - 6}>{r.label}</text>)}
        {rows.map((r, i) => (
          <rect key={`h${r.id || i}`} className="hit" x={x(i) - step / 2} width={step} y={PAD.t} height={IH_SHORT} onMouseEnter={() => setHi(i)} onClick={() => setHi(i)} />
        ))}
      </svg>
      {hi !== null && rows[hi] ? (
        <Tip leftPct={(x(hi) / W) * 100} title={rows[hi].tip || rows[hi].label}
          items={series.map((s) => ({ label: s.label, color: s.color, v: rows[hi][s.k] }))} />
      ) : null}
    </div>
  );
};

/* The exact counts behind a chart (the light-mode contrast relief: a number is never colour-only). */
const Numbers = ({ rows, series, extra = [] }) => (
  <details className="adm-sb-numbers" data-testid="sb-numbers">
    <summary>Numbers</summary>
    <div className="adm-sb-table-wrap">
      <table className="adm-sb-table">
        <thead><tr><th>{rows[0] && rows[0].kind === 'week' ? 'Week' : 'Day'}</th>{series.map((s) => <th key={s.k} title={s.label}>{s.short || s.label}</th>)}{extra.map((e) => <th key={e.k} title={e.label}>{e.short || e.label}</th>)}</tr></thead>
        <tbody>
          {rows.slice().reverse().map((r) => (
            <tr key={r.id}><td>{r.tip}</td>{series.map((s) => <td key={s.k}>{(r[s.k] || 0).toLocaleString()}</td>)}{extra.map((e) => <td key={e.k}>{e.fmt ? e.fmt(r) : r[e.k]}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  </details>
);

const S_PATH = [
  { k: 'web', label: 'Web', color: S(1) },
  { k: 'mcp', label: 'Claude / MCP', short: 'MCP', color: S(2) },
  { k: 'api', label: 'API key / plugin', short: 'API key', color: S(3) },
  { k: 'invite', label: 'Invite', color: S(4) },
];
const S_SIGNUPS = [...S_PATH, { k: 'duplicate', label: 'Duplicate account', short: 'Duplicate', color: GREY }];
const S_WEEK_TOTAL = [
  { k: 'real', label: 'Real signups', color: INK },
  { k: 'duplicate', label: 'Duplicate', color: GREY, dash: true },
];
const S_COUNTRY = [
  { k: 'us', label: 'US', color: S(1) },
  { k: 'paying', label: 'Paying countries', short: 'Paying', color: S(2) },
  { k: 'restricted', label: 'Restricted', color: S(3) },
  { k: 'other', label: 'Never-paid others', short: 'Never-paid', color: S(4) },
  { k: 'unknown', label: 'Unknown', color: GREY },
];
const S_DEVICE = [
  { k: 'desktop', label: 'Desktop', color: S(1) },
  { k: 'phone', label: 'Phone', color: S(2) },
  { k: 'tablet', label: 'Tablet', color: S(3) },
];
const S_FORK = [
  { k: 'employee', label: 'Hire door (intent, not a hire)', short: 'Hire door', color: S(1) },
  { k: 'task', label: 'One-time task', short: 'Task', color: S(2) },
  { k: 'key', label: 'API key', color: S(3) },
  { k: 'mcp', label: 'Claude (MCP)', short: 'MCP', color: S(4) },
  { k: 'skipped', label: 'Skipped', color: S(5) },
  { k: 'dropped', label: 'Opened, left', short: 'Left', color: S(6) },
  { k: 'never_shown', label: 'Never saw the fork', short: 'Never shown', color: GREY },
];
const S_HIRES = [
  { k: 'employees', label: 'AI employees hired', short: 'Employees', color: S(1) },
  { k: 'people', label: 'People who hired', short: 'People', color: S(2) },
];
const S_STARTER = [
  { k: 'used_all', label: 'Used all', color: S(1) },
  { k: 'used_some', label: 'Used some', color: S(2) },
  { k: 'used_none', label: 'Used none', color: S(3) },
  { k: 'no_grant', label: 'No grant', color: GREY },
];

/* Every series list on the page, for the swatch test (legend swatch fill === the series fill). */
export const SCOREBOARD_SERIES = [S_SIGNUPS, S_WEEK_TOTAL, S_PATH, S_COUNTRY, S_DEVICE, S_FORK, S_HIRES, S_STARTER];

const dayLabel = (iso) => String(iso || '').slice(5);
const weekTip = (w) => `Week of ${w.start}${w.partial ? ' (so far)' : ''}`;

/* Pure: the v1 cache block -> chart rows + tiles. Exported for the page test. */
export const buildScoreboardV1 = (v1) => {
  if (!v1 || !Array.isArray(v1.daily) || !Array.isArray(v1.weekly)) return null;
  const daily = v1.daily.map((d) => ({ id: d.date, kind: 'day', label: dayLabel(d.date), tip: d.date, real: d.real, duplicate: d.duplicate, ...d.path }));
  const fork = v1.daily.map((d) => ({ id: d.date, kind: 'day', label: dayLabel(d.date), tip: d.date, ...d.fork }));
  const starter = v1.daily.map((d) => ({ id: d.date, kind: 'day', label: dayLabel(d.date), tip: d.date, ...d.starter }));
  const wk = (w) => ({ id: w.week, kind: 'week', label: dayLabel(w.start), tip: weekTip(w) });
  const weekly = v1.weekly.map((w) => ({ ...wk(w), real: w.real, duplicate: w.duplicate, ...w.path }));
  const country = v1.weekly.map((w) => ({ ...wk(w), ...w.country }));
  const device = v1.weekly.map((w) => {
    const stamped = (w.device.desktop || 0) + (w.device.phone || 0) + (w.device.tablet || 0);
    return { ...wk(w), ...w.device, stamped, unknown: w.device_unknown || 0, noData: stamped === 0 };
  });
  const hires = v1.weekly.map((w) => ({ ...wk(w), employees: w.employees_hired, people: w.people_hired, first_time: w.first_time_hirers }));
  const t = v1.tiles || {};
  const sw = t.signups_week || {}; const up = t.us_paying_7d || {}; const fh = t.first_time_hirers_week || {};
  const thisWeek = v1.weekly[v1.weekly.length - 1] || {};
  const tiles = [
    {
      k: 'Real signups · this week', v: sw.this_week,
      d: `vs ${sw.last_week_same_days} the same ${sw.days_elapsed === 1 ? 'day' : `${sw.days_elapsed} days`} last week · last week ${sw.last_week ?? '—'} · duplicate this week ${thisWeek.duplicate ?? 0} (not counted)`,
    },
    {
      k: 'US + paying countries · last 7 days', v: typeof up.pct === 'number' ? `${up.pct}%` : '—',
      d: `${up.n} of ${up.of} real signups, ${up.first_day} to ${up.last_day} ET`,
    },
    {
      k: 'First-time hirers · this week', v: fh.this_week,
      d: `vs ${fh.last_week ?? '—'} last week · ${thisWeek.people_hired ?? 0} people hired ${thisWeek.employees_hired ?? 0} AI employees this week`,
    },
  ];
  return { daily, weekly, country, device, fork, hires, starter, tiles, payingCountries: v1.paying_countries || [], restrictedPayers: v1.restricted_countries_with_payers || [], grantSource: v1.starter_grant_source || {} };
};

const Panel = ({ title, sub, children }) => (
  <div className="adm-panel">
    <h3 className="adm-panel-h">{title}</h3>
    <p className="adm-panel-s">{sub}</p>
    {children}
  </div>
);

const AdminScoreboardPage = () => {
  const [full, setFull] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const res = await getScoreboardFull();
      setFull(res.data);
    } catch (e) {
      setError(String(e?.response?.data?.detail || e?.message || 'unavailable'));
    }
    setBusy(false);
  }, []);
  useEffect(() => { updateDocumentTitle('Business Scoreboard · Admin'); load(); }, [load]);

  const sb = useMemo(() => (full ? buildScoreboardV1(full.v1) : null), [full]);

  return (
    <div className="adm-page adm-sb">
      <p className="adm-crumb"><Link to="/admin">Admin</Link> › Business Scoreboard</p>
      <PageHeader
        title="Business Scoreboard"
        subtitle="The critical numbers, last 30 days. People, not events; real signups and duplicate accounts never blend. ET days, Monday to Sunday weeks."
        actions={<Button icon={<ReloadOutlined />} onClick={load} loading={busy}>Refresh</Button>}
      />
      <p className="adm-stamp">
        {full ? <span>Hourly cache · {ageText(full)} · same source as the 9am email · internal, test, locked and deleted accounts excluded server-side · hover or tap a chart for exact numbers</span> : <span>{error ? 'Not serving' : 'Loading…'}</span>}
      </p>
      {error && !full ? <div className="adm-empty">The scoreboard cache is not serving: {error}</div> : null}
      {full && !sb ? <div className="adm-empty">This cache was computed before the v1 panels shipped. They appear after the next hourly beat.</div> : null}
      {sb ? (
        <>
          <div className="adm-tiles">
            {sb.tiles.map((t) => (
              <div className="adm-tile" key={t.k}><div className="adm-tile-k">{t.k}</div><div className="adm-tile-v">{t.v ?? '—'}</div><div className="adm-tile-d">{t.d}</div></div>
            ))}
          </div>
          <div className="adm-panels">
            <Panel title="1 · Signups per day" sub="Real signups stacked by how they came in; duplicate accounts in grey on the same bar. Totals are for the 30 days.">
              <Bars rows={sb.daily} series={S_SIGNUPS} />
              <Legend rows={sb.daily} series={S_SIGNUPS} />
              <p className="adm-panel-s adm-sb-sub2">Weekly totals</p>
              <Lines rows={sb.weekly} series={S_WEEK_TOTAL} />
              <Legend rows={sb.weekly} series={S_WEEK_TOTAL} totals={false} />
              <Numbers rows={sb.daily} series={S_SIGNUPS} extra={[{ k: 'real', label: 'Real' }]} />
            </Panel>
            <Panel title="2 · Signup path, weekly" sub="First signal wins: Claude/MCP (consent-page landing, MCP door or token, MCP first chat), API key/plugin (key door, any API key, SDK first chat), invite link, else web.">
              <Bars rows={sb.weekly} series={S_PATH} labelEvery={1} />
              <Legend rows={sb.weekly} series={S_PATH} />
              <Numbers rows={sb.weekly} series={S_PATH} />
            </Panel>
            <Panel title="3 · Where real signups come from, weekly" sub={`Detected country, else profile country. Paying = a real payer in the last 90 days: ${sb.payingCountries.join(', ') || 'none'}. Restricted = the starter-grant list${sb.restrictedPayers.length ? ` (with payers, still restricted: ${sb.restrictedPayers.join(', ')})` : ''}.`}>
              <Bars rows={sb.country} series={S_COUNTRY} labelEvery={1} />
              <Legend rows={sb.country} series={S_COUNTRY} />
              <Numbers rows={sb.country} series={S_COUNTRY} />
            </Panel>
            <Panel title="4 · Desktop vs mobile, weekly share" sub="Device the account signed up on, recorded from Oct 2026. Weeks before that read no data, never a guess.">
              <Bars rows={sb.device} series={S_DEVICE} labelEvery={1} pct foot={(r) => `${r.stamped} recorded · ${r.unknown} no data`} />
              <Legend rows={sb.device} series={S_DEVICE} />
              <Numbers rows={sb.device} series={S_DEVICE} extra={[{ k: 'unknown', label: 'No data' }]} />
            </Panel>
            <Panel title="5 · Fork: what real signups chose, per day" sub="Bucket = the door submitted, else the last door opened. The hire door is intent, not a hire.">
              <Bars rows={sb.fork} series={S_FORK} />
              <Legend rows={sb.fork} series={S_FORK} />
              <Numbers rows={sb.fork} series={S_FORK} />
            </Panel>
            <Panel title="6 · Hires, weekly" sub="AI employees hired (deleted ones excluded) and the people who hired them. Owners are real accounts only.">
              <Lines rows={sb.hires} series={S_HIRES} />
              <Legend rows={sb.hires} series={S_HIRES} />
              <Numbers rows={sb.hires} series={S_HIRES} extra={[{ k: 'first_time', label: 'First-time hirers', short: 'First-time' }]} />
            </Panel>
            <Panel title="7 · Starter credits used, by signup day" sub="Each real signup against their own starter grant. Bought credits never count. Recent days are still spending.">
              <Bars rows={sb.starter} series={S_STARTER} />
              <Legend rows={sb.starter} series={S_STARTER} />
              <Numbers rows={sb.starter} series={S_STARTER} />
            </Panel>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default AdminScoreboardPage;
